"use client";

import { useEffect, useRef, useState } from "react";

import { galleryImages } from "@/data/gallery";

/* ─────────────────────────────────────────────────────
   ONE self-contained section:
   ┌─────────────────────────────────────────────────┐
   │  TOP HALF  — heading left, 4-col image grid right│
   ├─────────────────────────────────────────────────┤
   │  BOTTOM HALF — fullscreen scroll slideshow       │
   │  (sticky stage, 8 slides, dot + bar nav)         │
   └─────────────────────────────────────────────────┘
───────────────────────────────────────────────────── */

export function HandcraftedLuxury() {
  const TOTAL = galleryImages.length;
  const [active, setActive] = useState(0);
  const [entered, setEntered] = useState(false);

  /* slideshow refs */
  const wrapRef  = useRef<HTMLDivElement>(null); // scroll container
  const stageRef = useRef<HTMLDivElement>(null); // sticky stage

  /* ── update active index from scroll ── */
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    function onScroll() {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const { top, height } = wrap.getBoundingClientRect();
      // progress 0→1 through the scroll container
      const progress = Math.max(0, Math.min(1, -top / (height - window.innerHeight)));
      const idx = Math.min(TOTAL - 1, Math.floor(progress * TOTAL));
      setActive(idx);
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [TOTAL]);

  /* ── reveal grid images on first scroll into view ── */
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setEntered(true); },
      { threshold: 0.15 }
    );
    const el = document.getElementById("hl-grid");
    if (el) obs.observe(el);
    return () => obs.disconnect();
  }, []);

  /* ── jump to slide ── */
  function goTo(i: number) {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const { top } = wrap.getBoundingClientRect();
    const scrollH = wrap.scrollHeight - window.innerHeight;
    const target  = window.scrollY + top + (scrollH * i) / TOTAL;
    window.scrollTo({ top: target, behavior: "smooth" });
  }

  /* progress 0-100 for the bar */
  const barWidth = ((active + 1) / TOTAL) * 100;

  return (
    <section
      style={{
        width: "100%",
        background: "#f5f1eb",
        fontFamily: "'Inter','Helvetica Neue',sans-serif",
      }}
    >

      {/* ══════════════════════════════════════════════
          A.  INTRO  — heading left + grid right
          Plain document flow, no sticky/pin tricks
      ══════════════════════════════════════════════ */}
      <div
        style={{
          maxWidth: 1440,
          margin: "0 auto",
          padding: "100px 40px 80px",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 60,
          alignItems: "center",
        }}
        className="hl-intro"
      >
        {/* left — heading */}
        <div>
          <p
            style={{
              margin: "0 0 24px",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.4em",
              textTransform: "uppercase",
              color: "#c08a3e",
            }}
          >
            — Curated Visual Archive
          </p>

          <h2
            style={{
              margin: 0,
              fontSize: "clamp(3rem,5.5vw,6rem)",
              fontWeight: 900,
              lineHeight: 1,
              textTransform: "uppercase",
              letterSpacing: "-0.03em",
              color: "#1c1b19",
              fontFamily: "'Archivo Black','Arial Black',sans-serif",
            }}
          >
            The Art
            <br />
            <span
              style={{
                color: "transparent",
                WebkitTextStroke: "2px #1c1b19",
              }}
            >
              Of Hand
            </span>
            <span
              style={{
                display: "inline-block",
                width: "0.14em",
                height: "0.14em",
                borderRadius: "50%",
                background: "#c08a3e",
                verticalAlign: "middle",
                margin: "0 0.06em",
              }}
              aria-hidden
            />
            <span
              style={{
                color: "transparent",
                WebkitTextStroke: "2px #1c1b19",
              }}
            >
              crafted
            </span>
            <br />
            Luxury
          </h2>

          <p
            style={{
              margin: "32px 0 0",
              fontSize: 15,
              lineHeight: 1.75,
              color: "#5a5650",
              maxWidth: "36ch",
            }}
          >
            Every piece in our archive represents thousands of hours of
            mastery — tradition distilled into objects that outlast trends.
          </p>
        </div>

        {/* right — 4-col × 2-row image grid */}
        <div
          id="hl-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gridTemplateRows: "repeat(2, 1fr)",
            gap: 10,
          }}
        >
          {galleryImages.map((img, i) => (
            <div
              key={img.id}
              style={{
                aspectRatio: "3/4",
                borderRadius: 12,
                overflow: "hidden",
                background: "#ddd8cf",
                opacity: entered ? 1 : 0,
                transform: entered ? "translateY(0) scale(1)" : "translateY(24px) scale(0.96)",
                transition: `opacity 0.55s ${i * 0.06}s ease, transform 0.55s ${i * 0.06}s ease`,
              }}
            >
              <img
                src={img.src}
                alt={img.alt}
                loading={i < 4 ? "eager" : "lazy"}
                decoding="async"
                style={{ display: "block", width: "100%", height: "100%", objectFit: "cover" }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════════════
          B.  SLIDESHOW  — sticky stage inside tall wrapper
          wrapper height = TOTAL × 100vh
          stage sticks to top, images cross-fade
      ══════════════════════════════════════════════ */}
      <div
        ref={wrapRef}
        style={{
          position: "relative",
          height: `${TOTAL * 100}vh`,
          background: "#0e0d0b",
        }}
      >
        {/* sticky stage */}
        <div
          ref={stageRef}
          style={{
            position: "sticky",
            top: 0,
            width: "100%",
            height: "100vh",
            overflow: "hidden",
            background: "#0e0d0b",
          }}
        >

          {/* ── images: all stacked, only active is visible ── */}
          {galleryImages.map((img, i) => (
            <div
              key={img.id}
              aria-hidden={i !== active}
              style={{
                position: "absolute",
                inset: 0,
                zIndex: i === active ? 2 : 1,
                opacity: i === active ? 1 : 0,
                transition: "opacity 0.65s ease",
              }}
            >
              <img
                src={img.src}
                alt={img.alt}
                loading={i < 2 ? "eager" : "lazy"}
                decoding="async"
                style={{
                  display: "block",
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
              {/* dark gradient for text readability */}
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(to right, rgba(14,13,11,0.82) 0%, rgba(14,13,11,0.4) 50%, transparent 100%)",
                }}
              />
            </div>
          ))}

          {/* ── text panel — left side, transitions with active ── */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              padding: "0 clamp(24px, 6vw, 96px)",
              pointerEvents: "none",
            }}
          >
            {galleryImages.map((img, i) => (
              <div
                key={img.id}
                aria-hidden={i !== active}
                style={{
                  position: "absolute",
                  opacity: i === active ? 1 : 0,
                  transform: i === active ? "translateY(0)" : "translateY(20px)",
                  transition: "opacity 0.55s ease, transform 0.55s ease",
                  maxWidth: 520,
                }}
              >
                {/* category badge */}
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 16,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.4em",
                    textTransform: "uppercase",
                    color: "#c08a3e",
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: 24,
                      height: 1,
                      background: "#c08a3e",
                    }}
                  />
                  {img.category}
                </span>

                {/* counter */}
                <span
                  style={{
                    display: "block",
                    marginBottom: 8,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.3em",
                    color: "rgba(255,255,255,0.35)",
                  }}
                >
                  {String(i + 1).padStart(2, "0")} / {String(TOTAL).padStart(2, "0")}
                </span>

                {/* title */}
                <h3
                  style={{
                    margin: "0 0 16px",
                    fontSize: "clamp(2rem, 4.5vw, 4rem)",
                    fontWeight: 900,
                    textTransform: "uppercase",
                    letterSpacing: "-0.025em",
                    lineHeight: 1.05,
                    color: "#f0ece4",
                    fontFamily: "'Archivo Black','Arial Black',sans-serif",
                  }}
                >
                  {img.title}
                </h3>

                {/* caption */}
                <p
                  style={{
                    margin: 0,
                    fontSize: "clamp(0.875rem, 1.2vw, 1rem)",
                    lineHeight: 1.7,
                    color: "rgba(240,236,228,0.68)",
                    maxWidth: "34ch",
                  }}
                >
                  {img.caption}
                </p>
              </div>
            ))}
          </div>

          {/* ── dot nav — right side ── */}
          <nav
            aria-label="Gallery navigation"
            style={{
              position: "absolute",
              right: "clamp(16px, 3vw, 40px)",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 20,
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            {galleryImages.map((img, i) => (
              <button
                key={img.id}
                type="button"
                onClick={() => goTo(i)}
                aria-label={`Go to ${img.title}`}
                aria-current={i === active}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  background: i === active ? "#c08a3e" : "rgba(255,255,255,0.25)",
                  transform: i === active ? "scale(1.5)" : "scale(1)",
                  transition: "background 0.3s, transform 0.3s",
                  outline: "none",
                }}
              />
            ))}
          </nav>

          {/* ── bottom bar: title + progress + count ── */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              bottom: "clamp(20px, 3vh, 40px)",
              left: "clamp(24px, 6vw, 96px)",
              right: "clamp(24px, 6vw, 96px)",
              zIndex: 20,
              display: "flex",
              alignItems: "center",
              gap: 20,
            }}
          >
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.25em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.45)",
                whiteSpace: "nowrap",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 160,
              }}
            >
              {galleryImages[active].title}
            </span>

            {/* progress track */}
            <div
              style={{
                flex: 1,
                height: 2,
                background: "rgba(255,255,255,0.12)",
                borderRadius: 9999,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${barWidth}%`,
                  background: "#c08a3e",
                  borderRadius: 9999,
                  transition: "width 0.4s ease",
                }}
              />
            </div>

            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.2em",
                color: "rgba(255,255,255,0.45)",
                whiteSpace: "nowrap",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {String(active + 1).padStart(2, "0")} / {String(TOTAL).padStart(2, "0")}
            </span>
          </div>

        </div>{/* end sticky stage */}
      </div>{/* end slideshow wrapper */}

      {/* ══════════════════════════════════════════════
          C.  THUMBNAIL STRIP  — back on light bg
      ══════════════════════════════════════════════ */}
      <div
        style={{
          background: "#f5f1eb",
          padding: "80px 40px",
        }}
      >
        <div
          style={{
            maxWidth: 1440,
            margin: "0 auto",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              marginBottom: 28,
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.4em",
                textTransform: "uppercase",
                color: "#c08a3e",
              }}
            >
              Browse the Collection
            </p>
            <span
              style={{
                fontSize: 10,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: "#9e9a94",
              }}
            >
              Click to jump
            </span>
          </div>

          <div
            style={{
              display: "flex",
              gap: 12,
              overflowX: "auto",
              scrollSnapType: "x mandatory",
              WebkitOverflowScrolling: "touch",
              scrollbarWidth: "none",
              paddingBottom: 4,
            }}
          >
            {galleryImages.map((img, i) => (
              <div
                key={img.id}
                style={{
                  flex: "0 0 clamp(120px, 15vw, 190px)",
                  scrollSnapAlign: "start",
                }}
              >
                <button
                  type="button"
                  onClick={() => goTo(i)}
                  aria-label={`Go to ${img.title}`}
                  aria-current={i === active}
                  style={{
                    display: "block",
                    position: "relative",
                    width: "100%",
                    aspectRatio: "3/4",
                    overflow: "hidden",
                    borderRadius: 10,
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    background: "#ddd8cf",
                    outline: "none",
                    transform: i === active ? "translateY(-6px)" : "translateY(0)",
                    transition: "transform 0.3s ease",
                    boxShadow:
                      i === active
                        ? "0 16px 32px -8px rgba(28,27,25,0.28)"
                        : "0 4px 12px rgba(28,27,25,0.08)",
                  }}
                >
                  <img
                    src={img.src}
                    alt=""
                    aria-hidden
                    decoding="async"
                    style={{
                      display: "block",
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      filter: i === active ? "saturate(1.1)" : "saturate(0.65)",
                      transition: "filter 0.35s ease",
                    }}
                  />
                  {/* active ring */}
                  <span
                    aria-hidden
                    style={{
                      position: "absolute",
                      inset: 0,
                      borderRadius: 10,
                      border: `2.5px solid ${i === active ? "#c08a3e" : "transparent"}`,
                      transition: "border-color 0.25s",
                      pointerEvents: "none",
                    }}
                  />
                </button>

                <div style={{ marginTop: 8 }}>
                  <span
                    style={{
                      display: "block",
                      fontSize: 9,
                      fontWeight: 700,
                      letterSpacing: "0.25em",
                      textTransform: "uppercase",
                      color: i === active ? "#c08a3e" : "#9e9a94",
                      marginBottom: 2,
                      transition: "color 0.25s",
                    }}
                  >
                    {img.category}
                  </span>
                  <span
                    style={{
                      display: "block",
                      fontSize: 12,
                      fontWeight: 600,
                      color: i === active ? "#1c1b19" : "#9e9a94",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      transition: "color 0.25s",
                    }}
                  >
                    {img.title}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* mobile responsive overrides */}
      <style>{`
        @media (max-width: 768px) {
          .hl-intro {
            grid-template-columns: 1fr !important;
            padding: 60px 20px 50px !important;
            gap: 40px !important;
          }
        }
      `}</style>
    </section>
  );
}